import type { ThreadEnvMode } from "@cmmd-forge/contracts";
import type { ProjectGroupDeletionPlan } from "~/projectGroupDeletion";
import type { ProjectGroupingMode } from "~/projectGrouping";
import {
  DangerZoneSection,
  DefaultModelRow,
  ProjectIconRow,
  RemoveCheckoutRow,
  ProjectGroupingRow,
  ThreadWorkspaceRow,
  describeNameRow,
  type ProjectGroupingRowState,
  type ProjectThreadEnvModeState,
} from "./projectGroupSettingsRows";
import type { ProjectCanonicalIdentity } from "../ProjectIcon";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";
import type { CheckoutOption, CheckoutRemoval, GroupWritability } from "~/projectCheckoutOptions";
import type { ProjectDefaultModelPair, ProjectDefaultModelState } from "~/projectDefaultModel";
import { resolveProjectPlacement, type PlacementEnvironment } from "~/logicalProjectPlacement";
import { ProjectEnvironmentPlacementSection } from "./ProjectEnvironmentPlacementSection";
import { Input } from "../ui/input";
import { SettingsPageContainer, SettingsRow, SettingsSection } from "../settings/settingsLayout";
import { ProjectGroupRemovalDialog } from "./ProjectGroupRemovalDialog";
import {
  runProjectGroupRemoval,
  type ProjectGroupRemovalTarget,
} from "~/projectGroupRemovalRunner";
import {
  executeProjectCheckoutDeletion,
  previewProjectCheckoutDeletion,
  previewProjectCheckoutDeletionTargets,
} from "~/projectCheckoutDeletionClient";
import { ProjectGroupCheckoutPathRow } from "./ProjectGroupCheckoutPathRow";
import { removeProjectGroupMetadata } from "~/projectGroupMetadataRemoval";
import { resolveProjectGroupRemovalTargets } from "~/projectGroupRemovalTargets";
import { useProjectGroupRemovalCompletion } from "./projectGroupRemovalCompletion";
/**
 * The Environments this Seat owns, as forge can currently enumerate them: none.
 *
 * Not a stand-in for "the user has no Environments" — most Seats have one. It
 * is that forge has no way to ask. Every environment identifier it receives is
 * a `cmmd-runtime:` id naming the Sprite backing one Project, so counting them
 * would report one Environment per Project and show a placement chooser to
 * anyone with two cloud Projects — the precise failure this section exists to
 * avoid. FOR-32652 exposes Environment identity outward and FOR-32649 removes
 * the per-project keying; this constant is what they replace.
 *
 * Until then the section below renders nothing, which is the correct product
 * behaviour for every Seat that owns one Environment.
 */
const SEAT_PLACEMENT_ENVIRONMENTS: readonly PlacementEnvironment[] = [];

/** No Project has been assigned yet, because nothing can persist an assignment. */
const SEAT_PLACEMENT_ASSIGNMENTS = new Map<string, never>();

export interface ProjectGroupSettingsProps {
  readonly groupKey: string;
  readonly name: string;
  readonly checkouts: readonly CheckoutOption[];
  readonly selectedCheckoutValue: string;
  readonly threadCount: number;
  readonly threadCountByCheckoutValue: ReadonlyMap<string, number>;
  readonly defaultModel: ProjectDefaultModelState;
  readonly removal: CheckoutRemoval;
  readonly writability: GroupWritability;
  readonly threadEnvMode: ProjectThreadEnvModeState;
  /** CMMD's identity for this project, when it has one. */
  readonly canonicalIdentity: ProjectCanonicalIdentity | undefined;
  readonly deletion: ProjectGroupDeletionPlan;
  readonly grouping: ProjectGroupingRowState;
  readonly onSelectCheckout: (value: string) => void;
  readonly onRename: (name: string) => void;
  readonly onChangeDefaultModel: (selection: ProjectDefaultModelPair | null) => void;
  /** `null` unpins the project, restoring the app-wide setting. */
  readonly onChangeThreadEnvMode: (mode: ThreadEnvMode | null) => void;
  /** Deletes every checkout of this project, via the sidebar's own removal saga. */
  readonly onDeleteProject: () => Promise<void>;
  /** Removes exactly one checkout after its file deletion succeeds. */
  readonly onRemoveCheckoutTarget?: (target: ProjectGroupRemovalTarget) => Promise<void>;
  /** Removes one project record after its file deletion succeeds. */
  readonly onRemoveProjectTarget?: (target: ProjectGroupRemovalTarget) => Promise<void>;
  readonly onRemovalComplete?: () => void;
  readonly onChangeGrouping: (mode: ProjectGroupingMode) => void;
  readonly onRemoveCheckout: () => Promise<void>;
}

/**
 * Settings for a checkout GROUP, following t3code's project settings panel.
 *
 * Group-scoped rather than per-checkout because that is what the settings
 * actually are: a laptop clone, a desktop clone and a sprite's checkout of the
 * same repo are one project, and a name that applied to only one of them would
 * leave the other machines showing something else.
 *
 * Board properties (status, priority, lead, target date) are deliberately NOT
 * here — they belong to a CMMD board, and a group can have several.
 */
export function ProjectGroupSettings(props: ProjectGroupSettingsProps) {
  const removalFlow = useProjectGroupRemovalCompletion({
    groupKey: props.groupKey,
    projectName: props.name,
    onProjectRemovalComplete: props.onRemovalComplete,
  });
  const dialogMode = removalFlow.dialogMode;
  const selected =
    props.checkouts.find((option) => option.value === props.selectedCheckoutValue) ??
    props.checkouts[0] ??
    null;
  const dialogTargets = resolveProjectGroupRemovalTargets({
    checkouts: props.checkouts,
    selected,
    mode: dialogMode,
    threadCountByCheckoutValue: props.threadCountByCheckoutValue,
  });

  return (
    <SettingsPageContainer>
      <SettingsSection title="Project">
        <ProjectIconRow
          canonicalIdentity={props.canonicalIdentity}
          name={props.name}
          checkout={selected}
        />
        <SettingsRow
          title="Name"
          description={describeNameRow(props.writability)}
          control={
            <Input
              aria-label="Project name"
              defaultValue={props.name}
              // A rename writes to each checkout, so with nothing writable the
              // edit would be discarded in silence.
              disabled={props.writability.kind !== "writable"}
              onBlur={(event) => props.onRename(event.currentTarget.value)}
            />
          }
        />
        <ProjectGroupingRow grouping={props.grouping} onChange={props.onChangeGrouping} />
      </SettingsSection>

      <SettingsSection title="New threads">
        <DefaultModelRow
          defaultModel={props.defaultModel}
          writability={props.writability}
          onChange={props.onChangeDefaultModel}
        />
        <ThreadWorkspaceRow
          threadEnvMode={props.threadEnvMode}
          writability={props.writability}
          onChange={props.onChangeThreadEnvMode}
        />
      </SettingsSection>

      <ProjectEnvironmentPlacementSection
        environments={SEAT_PLACEMENT_ENVIRONMENTS}
        placement={resolveProjectPlacement({
          logicalProjectKey: props.groupKey,
          environments: SEAT_PLACEMENT_ENVIRONMENTS,
          assignments: SEAT_PLACEMENT_ASSIGNMENTS,
        })}
        onSelectEnvironment={() => {
          // Reachable only once SEAT_PLACEMENT_ENVIRONMENTS holds two or more,
          // which also requires a writer for the assignment. Throwing rather
          // than swallowing: silently discarding a customer's placement choice
          // would move their code somewhere they did not pick.
          throw new Error("Project placement cannot be persisted yet (FOR-32651).");
        }}
      />

      <SettingsSection
        title="Checkout"
        headerAction={
          props.checkouts.length > 0 ? (
            <Select
              value={selected?.value ?? ""}
              onValueChange={(value) => props.onSelectCheckout(String(value))}
            >
              <SelectTrigger className="max-w-64" aria-label="Selected checkout">
                {/* `shortLabel`, not `label`: this trigger is 256px and every
                    checkout of one repo shares a long path prefix, so the full
                    label ellipsised INSIDE that prefix and twenty worktrees
                    rendered identical text. */}
                <SelectValue>{selected?.shortLabel}</SelectValue>
              </SelectTrigger>
              <SelectPopup align="end" alignItemWithTrigger={false}>
                {props.checkouts.map((option) => (
                  <SelectItem key={option.value} hideIndicator value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
          ) : null
        }
      >
        {selected === null ? (
          <SettingsRow
            title="No checkout on this machine"
            description="Open this repository from a connection to work on it here."
          />
        ) : (
          <>
            <ProjectGroupCheckoutPathRow
              threadCount={props.threadCount}
              workspaceRoot={selected.workspaceRoot}
            />
            <RemoveCheckoutRow
              removal={props.removal}
              onRemove={() => Promise.resolve(removalFlow.setDialogMode("checkout"))}
            />
          </>
        )}
      </SettingsSection>

      <DangerZoneSection
        deletion={props.deletion}
        onDelete={() => Promise.resolve(removalFlow.setDialogMode("project"))}
      />
      {dialogMode !== null ? (
        <ProjectGroupRemovalDialog
          open
          mode={dialogMode}
          projectName={props.name}
          targets={dialogTargets}
          isExecuting={removalFlow.isRemoving}
          isPreviewing={removalFlow.isPreviewing}
          results={removalFlow.results}
          previews={removalFlow.previews}
          onOpenChange={(open) => {
            if (!open) removalFlow.closeDialog();
          }}
          onDeleteFilesChange={(checked) => {
            if (!checked) {
              removalFlow.setPreviews(new Map());
              removalFlow.setIsPreviewing(false);
              return;
            }
            removalFlow.setIsPreviewing(true);
            void previewProjectCheckoutDeletionTargets(dialogTargets)
              .then(removalFlow.setPreviews)
              .finally(() => removalFlow.setIsPreviewing(false));
          }}
          onConfirm={(input) => {
            void (async () => {
              removalFlow.setIsRemoving(true);
              try {
                if (!input.deleteFiles) {
                  const metadataResults = await removeProjectGroupMetadata({
                    targets: dialogTargets,
                    mode: dialogMode,
                    removeCheckout: props.onRemoveCheckout,
                    removeProject: props.onDeleteProject,
                  });
                  if (removalFlow.finishRemoval(dialogMode, metadataResults)) return;
                  removalFlow.setResults(metadataResults);
                  return;
                }
                const nextResults = await runProjectGroupRemoval({
                  targets: dialogTargets,
                  deleteFiles: true,
                  confirmation: input.confirmation,
                  preview: previewProjectCheckoutDeletion,
                  deleteFilesForCheckout: async (target, deletion) => ({
                    deletion: await executeProjectCheckoutDeletion({ target, ...deletion }),
                    // Mirrors the branch that call took rather than reporting
                    // one, which the client does not. See the same line in
                    // useSidebarProjectRemoval.ts for why the two agree.
                    metadataRemoved: target.kind === "cloud",
                  }),
                  removeMetadata: (target) =>
                    dialogMode === "project"
                      ? props.onRemoveProjectTarget
                        ? props.onRemoveProjectTarget(target)
                        : props.onDeleteProject()
                      : props.onRemoveCheckoutTarget
                        ? props.onRemoveCheckoutTarget(target)
                        : props.onRemoveCheckout(),
                });
                if (removalFlow.finishRemoval(dialogMode, nextResults)) return;
                removalFlow.setResults(nextResults);
                if (nextResults.some((result) => result.metadata !== "removed")) {
                  removalFlow.setPreviews(
                    await previewProjectCheckoutDeletionTargets(dialogTargets),
                  );
                }
              } finally {
                removalFlow.setIsRemoving(false);
              }
            })();
          }}
        />
      ) : null}
    </SettingsPageContainer>
  );
}
